import { useSearchParams } from "react-router-dom";

import { SearchParams } from "@/shared/environment";
import { actions, Datasource } from "@/shared/state/visualization";
import {
  useDatasources,
  useVizDispatch,
} from "@/shared/state/visualization/hooks";

import { useWorkspaceLoaderData } from "../loader";

/**
 * Returns `addFiles`, which merges file IDs into the open visualization
 * workspace's datasources — existing dataset sources and files are kept,
 * duplicates dropped — and rewrites the URL's dataset/file search params to
 * match, unless the workspace was opened from a shared workspace link.
 */
export function useAddFilesToWorkspace() {
  const dispatch = useVizDispatch();
  const [, setSearchParams] = useSearchParams();
  const workspaceLoaderData = useWorkspaceLoaderData();
  const datasources = useDatasources();

  const isSharedWorkspace =
    workspaceLoaderData.configSource?.type === "workspace";

  const addFiles = (newFileIds: string[]) => {
    // Keep every existing dataset source — a workspace can hold several
    // (e.g. when opened from an event spanning multiple datasets).
    const datasetSources = datasources.filter((ds) => ds.type === "dataset");

    const existingFileIds = datasources
      .filter((ds) => ds.type === "file")
      .map((ds) => ds.value.fileId);

    const allFileIds = [...new Set([...existingFileIds, ...newFileIds])];

    const newDatasources: Datasource[] = [
      ...datasetSources,
      ...allFileIds.map((fileId) => ({
        type: "file" as const,
        value: { fileId },
      })),
    ];

    dispatch(actions.setDatasources(newDatasources));

    // A workspace opened from a shared workspace link gets its contents from
    // the persisted workspace record, so dataset/file search params don't
    // apply to it.
    if (isSharedWorkspace) {
      return;
    }

    setSearchParams(
      (oldParams) => {
        const params = new URLSearchParams(oldParams);

        params.delete(SearchParams.DATASET);
        datasetSources.forEach((ds) => {
          params.append(SearchParams.DATASET, ds.value.datasetId);
        });

        params.delete(SearchParams.FILE);
        allFileIds.forEach((fileId) => {
          params.append(SearchParams.FILE, fileId);
        });

        return params;
      },
      { preventScrollReset: true, replace: true },
    );
  };

  return { addFiles };
}
